import { useState } from "react";
import {
    Field,
    DateInput,
    Actions,
    PrimaryButton,
    GhostButton,
    BusyNote,
    ProgressBar,
    CardIntro,
} from "./ui/Form";
import { useNavigate } from "react-router-dom";
import api from "../api";

interface PositiveSample {
    objectId: number;
    agency_pool_num?: string;
    x: number;
    y: number;
    subgrid_label?: string;
    collection_date: string;
    add_date: string;
    wnv_positive: boolean;
    slev_positive: boolean;
    weev_positive: boolean;
    diseases: string[];
    coordinate_precision?: string;
    comments?: string;
}

interface MapGenerationResult {
    status: "success" | "failure";
    message: string;
    total_samples?: number;
    positive_samples?: number;
    samples?: PositiveSample[];
    date_range?: {
        start_date: string;
        end_date: string;
    };
    layer_info?: {
        title: string;
        feature_count: number;
        fields: string[];
    };
}

export default function DiseaseMapGenerationCard() {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
    const [result, setResult] = useState<MapGenerationResult | null>(null);

    // Set default date range to current week
    const getCurrentWeekRange = () => {
        const now = new Date();
        const dayOfWeek = now.getDay();
        
        // Find the most recent Saturday (end of the last complete Sunday-Saturday week)
        const lastSaturday = new Date(now);
        const daysToSubtract = dayOfWeek === 0 ? 1 : dayOfWeek + 1; // If Sunday (0), go back 1 day. Otherwise, go back dayOfWeek + 1 days
        lastSaturday.setDate(now.getDate() - daysToSubtract);
        lastSaturday.setHours(23, 59, 59, 999);
        
        // Find the Sunday that started that week (6 days before Saturday)
        const lastSunday = new Date(lastSaturday);
        lastSunday.setDate(lastSaturday.getDate() - 6);
        lastSunday.setHours(0, 0, 0, 0);
        
        return {
            start: lastSunday.toISOString().split('T')[0],
            end: lastSaturday.toISOString().split('T')[0]
        };
    };

    // Initialize with latest complete week (Sunday-Saturday) if dates are empty
    const initializeCurrentWeek = () => {
        const weekRange = getCurrentWeekRange();
        setStartDate(weekRange.start);
        setEndDate(weekRange.end);
    };

    const handleAnalyzePositives = async () => {
        if (!startDate || !endDate) {
            setResult({
                status: "failure",
                message: "Please select both start and end dates"
            });
            return;
        }

        setIsAnalyzing(true);
        setResult(null);

        try {
            const response = await api.post("/analyze-disease-positives", {
                start_date: startDate,
                end_date: endDate
            });

            setResult(response.data);
            // Hand this week's positives to the Treatment Area Builder immediately
            if (response.data?.status === "success" && response.data.samples?.length) {
                const samples: PositiveSample[] = response.data.samples;
                localStorage.setItem('pioneer_positive_subgrids', JSON.stringify(
                    Array.from(new Set(samples.map(s => s.subgrid_label).filter(l => l && l !== 'Unknown')))
                ));
                localStorage.setItem('pioneer_positive_samples', JSON.stringify(
                    samples.map(s => ({ pool: s.agency_pool_num, x: s.x, y: s.y, subgrid: s.subgrid_label, diseases: s.diseases, precision: s.coordinate_precision || 'Exact', comments: s.comments || '' }))
                ));
            }
        } catch (err: any) {
            const msg = err.response?.data || { 
                status: "failure", 
                message: err.message || "Unknown error occurred" 
            };
            setResult(msg);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const formatDate = (dateString: string) => {
        // "2026-08-16" parsed by Date() is UTC midnight, which displays as the
        // 15th anywhere behind UTC -- the summary used to report the range one
        // day earlier than the date pickers. Build it as a local date instead.
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString ?? "");
        const d = m
            ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
            : new Date(dateString);
        return isNaN(d.getTime()) ? String(dateString ?? "") : d.toLocaleDateString();
    };

    const getDiseaseIcon = (diseases: string[]) => {
        if (diseases.includes("WNV")) return "🦟";
        if (diseases.includes("SLEV")) return "🔴";
        if (diseases.includes("WEEV")) return "🟡";
        return "❓";
    };

    const hasPositiveSamples = result?.status === "success" && result?.positive_samples && result.positive_samples > 0;

    const handleDownloadShapefile = async (type: 'points' | 'polygons') => {
        if (!result?.samples || result.samples.length === 0) {
            alert('No positive samples available for export');
            return;
        }

        try {
            setIsAnalyzing(true); // Reuse the loading state
            
            const response = await api.post(`/export-${type}-shapefile`, {
                start_date: startDate,
                end_date: endDate,
                samples: result.samples
            }, {
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename with today's date
            const today = new Date().toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '');
            const fileType = type === 'points' ? 'Pools_Pos' : 'Subgrids_Pos';
            link.download = `${fileType}_${today}.zip`;
            
            link.click();
            window.URL.revokeObjectURL(url);
            
        } catch (error: any) {
            console.error(`Error downloading ${type} shapefile:`, error);
            alert(`Failed to download ${type} shapefile. Please try again.`);
        } finally {
            setIsAnalyzing(false);
        }
    };

    /** Carry this week's positives into the Treatment Area Builder. Named for the old flow, when
     *  it jumped straight to spray notifications; areas are now finalized and scheduled first. */
    const handleSprayNotifications = async () => {
        if (!result?.samples || result.samples.length === 0) {
            alert('No positive samples available — run the analysis first');
            return;
        }

        try {
            setIsAnalyzing(true);
            
            // Get the positive subgrids from the backend
            const response = await api.post('/get-positive-subgrids', {
                samples: result.samples
            });

            if (response.data.status === 'success') {
                const positiveSubgrids = response.data.positive_subgrids;
                
                // Store data in localStorage for the spray notifications page
                const sprayData = {
                    subgrids: positiveSubgrids.join(', '),
                    start_date: startDate,
                    end_date: endDate,
                    timestamp: Date.now()
                };
                // Legacy handover, kept only as a fallback if nothing gets scheduled this week
                localStorage.setItem('spray_notifications_prefill', JSON.stringify(sprayData));
                // This week's positives for the Treatment Area Builder (not cleared on use)
                localStorage.setItem('pioneer_positive_subgrids', JSON.stringify(positiveSubgrids));
                localStorage.setItem('pioneer_positive_samples', JSON.stringify(
                    (result?.samples || []).map(s => ({
                        pool: s.agency_pool_num, x: s.x, y: s.y,
                        subgrid: s.subgrid_label, diseases: s.diseases,
                        precision: s.coordinate_precision || 'Exact',
                        comments: s.comments || '',
                    }))
                ));
                
                // Positives now feed the Treatment Builder first — areas are finalized and
                // scheduled there, and the notification list is derived from what was actually
                // scheduled rather than from where the positives were.
                // Client-side nav (no full reload) so it works on the static host and keeps the session
                navigate('/builder');

            } else {
                alert('Failed to get positive subgrids. Please try again.');
            }
            
        } catch (error: any) {
            console.error('Error getting positive subgrids:', error);
            alert('Failed to open spray notifications. Please try again.');
        } finally {
            setIsAnalyzing(false);
        }
    };

    return (
        <div className="space-y-4">
            <CardIntro>
                Finds WNV, SLEV and WEEV positive samples in the pools layer for a date
                range, and pulls their coordinates so the maps and the notification list
                can be built from them.
            </CardIntro>

            <div className="grid grid-cols-1 min-[560px]:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <Field label="Start date">
                    <DateInput
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isAnalyzing}
                    />
                </Field>
                <Field label="End date">
                    <DateInput
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isAnalyzing}
                    />
                </Field>
                <GhostButton
                    onClick={initializeCurrentWeek}
                    disabled={isAnalyzing}
                    title="Select the latest complete Sunday-Saturday week"
                >
                    Latest week
                </GhostButton>
            </div>

            <Actions>
                <PrimaryButton
                    onClick={handleAnalyzePositives}
                    busy={isAnalyzing}
                    busyLabel="Analyzing…"
                    disabled={!startDate || !endDate}
                >
                    Analyze disease positives
                </PrimaryButton>
            </Actions>

            {isAnalyzing && <ProgressBar />}
            {isAnalyzing && (
                <BusyNote>Reading the pools layer and filtering by date range.</BusyNote>
            )}

            {/* Results Section */}
            {result && (
                                <div className="mt-2">
                    <h3 className="font-extrabold text-[15px] mb-2">Results</h3>
                    <div className={`p-4 rounded-sm2 text-sm border animate-[fadeIn_.2s_ease-out_both] ${
                        result.status === "success"
                            ? hasPositiveSamples
                                ? "bg-wnv-tint border-line-strong"
                                : "bg-brand-tint border-line-strong"
                            : "bg-red-50 border-red-200"
                    }`}>
                        {result.status === "success" ? (
                            <div>
                                {/* Layer Info */}
                                {result.layer_info && (
                                                                        <div className="mb-4 p-3 bg-paper-2 rounded-sm2">
                                        <p className="font-semibold">Layer information</p>
                                        <div className="mt-2 text-ink-soft">
                                            <p><strong>Title:</strong> {result.layer_info.title}</p>
                                            <p><strong>Total Features:</strong> {result.layer_info.feature_count}</p>
                                            <p><strong>Fields:</strong> {result.layer_info.fields.join(", ")}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Summary Stats */}
                                <div className="mb-4">
                                    <p className="font-semibold">
                                        Analysis summary ({formatDate(result.date_range?.start_date || startDate)} &ndash; {formatDate(result.date_range?.end_date || endDate)})
                                    </p>
                                    <div className="mt-2 grid grid-cols-2 gap-3">
                                        <div className="bg-card p-3 rounded-sm2 border border-line-strong">
                                            <p className="eyebrow">Total samples</p>
                                            <p className="text-2xl font-extrabold mt-0.5">{result.total_samples || 0}</p>
                                        </div>
                                        <div className="bg-card p-3 rounded-sm2 border border-line-strong">
                                            <p className="eyebrow">Disease positives</p>
                                            <p className={`text-2xl font-extrabold mt-0.5 ${hasPositiveSamples ? "text-wnv" : "text-brand-deep"}`}>
                                                {result.positive_samples || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Positive Samples Detail */}
                                {hasPositiveSamples && result.samples && (
                                    <div>
                                        <p className="font-semibold mb-2">
                                            Positive samples found
                                        </p>
                                        <div className="max-h-64 overflow-y-auto">
                                            <table className="w-full text-xs">
                                                <thead className="bg-paper-2 sticky top-0">
                                                    <tr>
                                                        <th className="px-2 py-1 text-left">Pool ID</th>
                                                        <th className="px-2 py-1 text-left">Subgrid</th>
                                                        <th className="px-2 py-1 text-left">Coordinates</th>
                                                        <th className="px-2 py-1 text-left">Collection Date</th>
                                                        <th className="px-2 py-1 text-left">Diseases</th>
                                                        <th className="px-2 py-1 text-left">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {result.samples.map((sample, index) => (
                                                        <tr key={index} className="border-t">
                                                            <td className="px-2 py-1">{sample.agency_pool_num || sample.objectId}</td>
                                                            <td className="px-2 py-1 font-semibold text-brand-deep">
                                                                {sample.subgrid_label || 'Unknown'}
                                                            </td>
                                                            <td className="px-2 py-1 font-mono">
                                                                {sample.x?.toFixed(6)}, {sample.y?.toFixed(6)}
                                                            </td>
                                                            <td className="px-2 py-1">{formatDate(sample.collection_date)}</td>
                                                            <td className="px-2 py-1">
                                                                {getDiseaseIcon(sample.diseases)} {sample.diseases.join(", ")}
                                                            </td>
                                                            <td className="px-2 py-1">
                                                                <button 
                                                                    onClick={() => alert(`Generate map for Pool ${sample.agency_pool_num || sample.objectId}`)}
                                                                    className="bg-card border border-line-strong hover:border-brand text-ink text-xs px-2 py-1 rounded-sm2 transition"
                                                                >
                                                                    📍 Generate Map
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Next Steps */}
                                                                                <div className="mt-4 rounded-sm2 border border-line-strong p-3">
                                            <p className="font-semibold text-[13px]">Next</p>
                                            <p className="text-ink-soft text-[13px] mt-0.5 mb-3">
                                                Download the shapefile for ArcGIS Pro, or carry these
                                                positives straight into the Treatment Area Builder.
                                            </p>
                                            <Actions>
                                                <GhostButton onClick={() => handleDownloadShapefile('points')}>
                                                    Positive samples shapefile
                                                </GhostButton>
                                                <PrimaryButton
                                                    onClick={handleSprayNotifications}
                                                    title="Carry these positives into the Treatment Area Builder. The notification list comes later, from the areas you actually schedule."
                                                >
                                                    Build treatment areas
                                                </PrimaryButton>
                                            </Actions>
                                        </div>
                                    </div>
                                )}

                                {/* No Positives Found */}
                                {!hasPositiveSamples && (
                                    <div className="text-brand-deep">
                                        <p className="font-medium">✅ No Disease Positives Found</p>
                                        <p className="mt-1">
                                            No WNV, SLEV, or WEEV positive samples were found in the selected date range.
                                            {result.total_samples && result.total_samples > 0 && (
                                                <span> All {result.total_samples} samples tested negative.</span>
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-wnv">
                                <p className="font-medium">❌ Analysis Failed</p>
                                <p className="mt-1">{result.message}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* What this does, in order. The old copy told people to enter a
                Pools Layer ID -- there has been no such field for a while. */}
            <div className="rounded-sm2 border border-line-strong p-3 text-[13px] space-y-1">
                <div className="font-semibold">How this fits together</div>
                <p className="text-ink-soft">
                    <strong>1.</strong> Pick the date range, or use <em>Latest week</em> for the
                    most recent complete Sunday&ndash;Saturday.
                </p>
                <p className="text-ink-soft">
                    <strong>2.</strong> Analyze &mdash; reads the pools layer and finds WNV, SLEV
                    and WEEV positives with coordinates.
                </p>
                <p className="text-ink-soft">
                    <strong>3.</strong> Download the shapefile for mapping, or send the positives
                    straight to the spray notification list.
                </p>
            </div>
        </div>
    );
}
